import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { WhatsAppSettings } from "./useWhatsApp";

/**
 * Public-facing WhatsApp settings hook for the Chat-on-WhatsApp button.
 *
 * Hard guarantee: this hook MUST only read from the
 * `get_active_whatsapp_settings()` SECURITY DEFINER RPC. The underlying
 * `whatsapp_settings` table is admin-only by RLS and exposing it from a
 * public component would leak admin contact data.
 *
 * To enforce that at runtime, while this query is in flight we wrap
 * `supabase.from` with a Proxy that throws if anything in the call path
 * (this hook, helpers it imports, future regressions) tries to touch the
 * `whatsapp_settings` table directly. Other tables remain unaffected and
 * the original `from` is restored in `finally`, so unrelated queries
 * running in parallel are not impacted.
 */
export const useWhatsAppPublicSettings = () => {
  return useQuery({
    queryKey: ["whatsapp-settings", "public-rpc-only"],
    queryFn: async () => {
      const originalFrom = supabase.from.bind(supabase);

      // Install temporary guard
      (supabase as unknown as { from: (...args: unknown[]) => unknown }).from =
        ((relation: string, ...rest: unknown[]) => {
          if (relation === "whatsapp_settings") {
            throw new Error(
              "WhatsApp public button must use get_active_whatsapp_settings() RPC, not the whatsapp_settings table.",
            );
          }
          return (originalFrom as unknown as (...a: unknown[]) => unknown)(
            relation,
            ...rest,
          );
        }) as unknown as typeof supabase.from;

      try {
        const { data, error } = await supabase.rpc(
          "get_active_whatsapp_settings",
        );
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        return (row ?? null) as WhatsAppSettings | null;
      } finally {
        // Always restore so other queries are unaffected
        (supabase as unknown as { from: typeof supabase.from }).from =
          originalFrom;
      }
    },
  });
};
